import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Footprints, Flame, Zap, Trophy, Medal, TrendingUp, MapPin, Mountain, Award, Lock, Share2, Download, Gift, Sparkles, Star } from "lucide-react";

const ICONS = { Footprints, Flame, Zap, Trophy, Medal, TrendingUp, MapPin, Mountain, Award };
const OBSIDIAN = "#0A192F";
const SAND = "#EDE7DA";
const SAGE = "#8AA79B";

// Dessine et télécharge une carte de partage 1080x1080 aux couleurs de l'app.
function downloadCard(badge, stats) {
  const c = document.createElement("canvas");
  c.width = 1080; c.height = 1080;
  const x = c.getContext("2d");
  x.fillStyle = OBSIDIAN; x.fillRect(0, 0, 1080, 1080);
  // halo
  const g = x.createRadialGradient(540, 380, 60, 540, 380, 620);
  g.addColorStop(0, "rgba(138,167,155,0.28)"); g.addColorStop(1, "rgba(10,25,47,0)");
  x.fillStyle = g; x.fillRect(0, 0, 1080, 1080);
  x.strokeStyle = "rgba(237,231,218,0.18)"; x.lineWidth = 2; x.strokeRect(60, 60, 960, 960);
  x.textAlign = "center";
  x.fillStyle = SAGE; x.font = "600 30px Arial"; x.fillText("BADGE DÉBLOQUÉ", 540, 200);
  // médaille
  x.beginPath(); x.arc(540, 380, 120, 0, Math.PI * 2); x.fillStyle = "rgba(138,167,155,0.15)"; x.fill();
  x.strokeStyle = SAGE; x.lineWidth = 4; x.stroke();
  x.fillStyle = SAND; x.font = "700 90px Arial"; x.fillText("★", 540, 415);
  x.fillStyle = SAND; x.font = "700 60px Arial"; x.fillText(badge.label, 540, 590);
  x.fillStyle = "rgba(237,231,218,0.7)"; x.font = "400 30px Arial";
  x.fillText(badge.desc || "", 540, 645);
  // stats
  const items = [["Séances", stats.sessions ?? 0], ["Distance", `${stats.distance ?? 0} km`], ["Cycles", stats.cycles ?? 0]];
  const sx = [300, 540, 780];
  items.forEach(([l, v], i) => {
    x.fillStyle = SAND; x.font = "700 54px Arial"; x.fillText(String(v), sx[i], 790);
    x.fillStyle = "rgba(237,231,218,0.55)"; x.font = "400 26px Arial"; x.fillText(l, sx[i], 835);
  });
  x.fillStyle = SAGE; x.font = "700 44px Arial"; x.fillText("MAIRUNCOACH", 540, 970);
  const url = c.toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url; a.download = `mairuncoach-${badge.id}.png`; a.click();
}

export default function GamificationSection() {
  const [g, setG] = useState(null);
  const introShown = useRef(false);

  const load = () => api.get("/gamification").then((r) => setG(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (g?.show_intro && !introShown.current) {
      introShown.current = true;
      toast.success("Tu as débloqué ton premier badge ! 🎉", {
        description: `Télécharge une carte de partage pour gagner ${g.points_per_download} points. À ${g.points_threshold} points, tu débloques -${g.discount_pct}% sur ton prochain cycle.`,
        duration: 9000,
      });
      api.post("/gamification/seen").catch(() => {});
    }
  }, [g]);

  const share = async (badge) => {
    downloadCard(badge, g.stats);
    try {
      const r = await api.post("/gamification/card-downloaded");
      if (r.data.just_unlocked) {
        toast.success(`Réduction débloquée ! 🎁 Code ${r.data.discount_code} = -${r.data.discount_pct}% sur ton prochain cycle.`, { duration: 10000 });
      } else {
        toast.success(`Carte téléchargée · +${g.points_per_download} points ! (${r.data.points}/${r.data.points_threshold})`);
      }
      load();
    } catch { toast.error("Erreur en enregistrant tes points"); }
  };

  const shareX = (badge) => {
    const text = encodeURIComponent(`J'ai débloqué le badge « ${badge.label} » sur MAIRUNCOACH ! 🏃`);
    window.open(`https://twitter.com/intent/tweet?text=${text}`, "_blank");
  };

  if (!g) return null;
  const unlocked = g.badges.filter((b) => b.unlocked);

  return (
    <div className="mt-6 rounded-2xl border border-border bg-white p-6" data-testid="gamification-section">
      <div className="flex items-center gap-2"><Sparkles size={18} className="text-sage" /><h3 className="text-lg font-medium">Récompenses & partage</h3></div>

      {/* Progression vers la réduction */}
      <div className="mt-4 rounded-xl bg-obsidian text-white p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm flex items-center gap-2"><Gift size={16} className="text-sage" /> Réduction -{g.discount_pct}% sur ton prochain cycle</div>
          <div className="text-sm font-semibold" data-testid="gamif-points">{g.points} / {g.points_threshold} pts</div>
        </div>
        <div className="mt-3 h-2.5 rounded-full bg-white/12 overflow-hidden">
          <div className="h-full bg-sage transition-all" style={{ width: `${g.progress_pct}%` }} data-testid="gamif-progress" />
        </div>
        {g.discount_code ? (
          <div data-testid="gamif-discount-code" className="mt-3 text-sm">🎁 Réduction prête ! Utilise le code <span className="font-mono font-bold bg-white/15 px-2 py-0.5 rounded">{g.discount_code}</span> au paiement de ton prochain cycle.</div>
        ) : (
          <p className="mt-3 text-xs text-white/60">Plus que {g.points_to_reward} points. Gagne {g.points_per_download} points à chaque carte de partage téléchargée.</p>
        )}
      </div>

      <p className="mt-4 text-sm text-obsidian/60">
        <strong className="text-obsidian">Comment gagner des points ?</strong> Débloque des badges en t'entraînant, puis télécharge ta carte de partage : chaque téléchargement = <strong>{g.points_per_download} points</strong>. À <strong>{g.points_threshold} points</strong>, tu reçois un code <strong>-{g.discount_pct}%</strong> pour ton prochain cycle.
      </p>

      {/* Galerie de badges */}
      <div className="mt-5">
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-obsidian/45 mb-3">Ma galerie de badges ({unlocked.length}/{g.badges.length})</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="badge-gallery">
          {g.badges.map((b) => {
            const Icon = ICONS[b.icon] || Award;
            return (
              <div key={b.id} data-testid={`badge-${b.id}`} title={b.desc}
                className={`rounded-xl border p-3 text-center transition-colors ${b.unlocked ? "border-sage/40 bg-sage/8" : "border-border bg-obsidian/[0.02] opacity-60"}`}>
                <div className="flex justify-center">
                  {b.unlocked ? <Icon size={26} className="text-sage" /> : <Lock size={22} className="text-obsidian/30" />}
                </div>
                <div className="mt-1.5 text-[11px] font-medium leading-tight">{b.label}</div>
                {!b.unlocked && <div className="mt-0.5 text-[10px] text-obsidian/40">{b.progress}/{b.threshold}</div>}
                {b.unlocked && (
                  <div className="mt-2 flex items-center justify-center gap-1.5">
                    <button data-testid={`share-${b.id}`} onClick={() => share(b)} title="Télécharger la carte"
                      className="inline-flex items-center gap-1 rounded-full bg-obsidian text-white px-2.5 py-1 text-[10px] font-medium hover:-translate-y-0.5 transition-transform"><Download size={11} /> Carte</button>
                    <button data-testid={`sharex-${b.id}`} onClick={() => shareX(b)} title="Partager sur X"
                      className="inline-flex items-center rounded-full border border-border px-2 py-1 text-[10px] hover:border-obsidian/40"><Share2 size={11} /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
